import { useUIStore } from '../../stores/uiStore';
import { Sun, Moon } from 'lucide-react';
import { useEffect } from 'react';

export function ThemeToggle() {
  const { darkMode, toggleDarkMode, setDarkMode } = useUIStore();

  // Initialize dark mode on mount based on system preference or saved state
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;

    if (savedDarkMode) {
      // Apply saved preference
      document.documentElement.classList.add('dark');
    } else {
      // Check system preference only if no saved preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark && !useUIStore.getState().darkMode) {
        // Optional: auto-enable based on system
        // setDarkMode(true);
      }
    }
  }, [setDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only auto-switch if user hasn't manually set a preference
      const hasPreference = localStorage.getItem('ui-storage');
      if (!hasPreference && e.matches) {
        setDarkMode(true);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
  }, [setDarkMode]);

  return (
    <button
      onClick={toggleDarkMode}
      className="relative inline-flex h-9 w-16 items-center rounded-full bg-neutral-200 dark:bg-neutral-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      aria-label="Toggle dark mode"
    >
      {/* Sun Icon (Light Mode) */}
      <span
        className={`absolute left-1.5 transition-all duration-300 transform ${
          !darkMode ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
        }`}
      >
        <Sun className="h-4 w-4 text-yellow-500" />
      </span>

      {/* Moon Icon (Dark Mode) */}
      <span
        className={`absolute right-1.5 transition-all duration-300 transform ${
          darkMode ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
        }`}
      >
        <Moon className="h-4 w-4 text-blue-300" />
      </span>

      {/* Toggle Slider */}
      <span
        className={`absolute h-7 w-7 rounded-full bg-white shadow-md transform transition-all duration-300 ${
          darkMode ? 'translate-x-9' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export function ThemeToggleCompact() {
  const { darkMode, toggleDarkMode } = useUIStore();

  return (
    <button
      onClick={toggleDarkMode}
      className="p-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {darkMode ? (
        <Sun className="h-5 w-5 text-yellow-400" />
      ) : (
        <Moon className="h-5 w-5 text-neutral-600" />
      )}
    </button>
  );
}

export function ThemeToggleIconOnly() {
  const { darkMode, toggleDarkMode } = useUIStore();

  return (
    <button
      onClick={toggleDarkMode}
      className="p-1.5 rounded hover:bg-white/20 transition-colors duration-200"
      aria-label="Toggle dark mode"
    >
      {darkMode ? (
        <Sun className="h-4 w-4 text-yellow-300" />
      ) : (
        <Moon className="h-4 w-4 text-white/80" />
      )}
    </button>
  );
}
